import { computed, Injectable, signal } from '@angular/core';
import { LoggerService } from '../logger/logger.service';
import { UserApiService } from './user-api.service';
import { PaginatedUsersDto, UserDto, UserParamsDto } from '../../models/user/user.dto';
import { catchError, firstValueFrom, map, Observable, throwError } from 'rxjs';
import { LogsType } from '../../../shared/constants/logs-type.enum';
import { CreateUserDto } from '../../models/user/create-user.dto';
import { UserError } from '../../errors/user-error.class';
import { UserErrorType } from '../../../shared/constants/error-status.enum';
import { UpdateUserDto } from '../../models/user/update-user.dto';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly className = 'UserService';
  private functionName = '';

  private readonly _currentUserRoleSignal = signal<string>('');
  readonly currentUserRoleSignal = computed(() => this._currentUserRoleSignal());

  constructor(
    private readonly logger: LoggerService,
    private readonly userApiService: UserApiService
  ) { }

  async loadCurrentUserContext(email: string): Promise<void> {
    this.functionName = 'loadCurrentUserContext';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    this._currentUserRoleSignal.set(sessionStorage.getItem('userRole') ?? '');

    if (this._currentUserRoleSignal()) {
      this.logger.logMessage(LogsType.INFO, this.className, this.functionName, `User context already loaded: ${this._currentUserRoleSignal()}`);
      return;
    }

    const response = await firstValueFrom(
      this.getUsersByEmail(email)
    );

    const user = response?.users?.[0];

    if (user?.role) {
      this._currentUserRoleSignal.set(user.role);
      sessionStorage.setItem('userRole', user.role);
    }
  }

  resetUserContext(): void {
    this.functionName = 'resetUserContext';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    this._currentUserRoleSignal.set('');
    sessionStorage.removeItem('userRole');
  }

  /**
   * Retrieves users based on the provided parameters.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userService.getUserByParams(params);
   *
   * // Use this in Interceptors, Guards and Functions
   * userService.getUserByParams(params);
   * ```
   *
   * @param params - The parameters to filter and paginate the users
   * @returns Observable<PaginatedUsersDto> - The users and the pagination information
   */
  private getUserByParams(params: UserParamsDto): Observable<PaginatedUsersDto> {
    this.functionName = 'getUserByParams';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return this.userApiService.getApiUsers(params).pipe(
      map((response: PaginatedUsersDto) => {
        if (response && response.users.length > 0) {
          this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'Users:', response.users);
          return { users: response.users, pagination: response.pagination };
        }

        this.logger.logMessage(LogsType.WARN, this.className, this.functionName, 'No users were returned');
        return { users: [], pagination: response.pagination };
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Retrieves users by their email.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userService.getUsersByEmail(userEmail);
   *
   * // Use this in Interceptors, Guards and Functions
   * userService.getUsersByEmail(userEmail);
   * ```
   *
   * @param userEmail - The email of the user to retrieve (optional)
   * @returns Observable<PaginatedUsersDto> - The users and the pagination information
   */
  getUsersByEmail(userEmail?: string): Observable<PaginatedUsersDto> {
    this.functionName = 'getUsersByEmail';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const params: UserParamsDto = {
      Email: userEmail ?? ''
    };

    return this.getUserByParams(params).pipe(
      map((response: PaginatedUsersDto) => response),
      catchError((error) => throwError(() => error))
    );
  }

  getCurrentUserRole(currentUserEmail: string): Observable<string> {
    this.functionName = 'getUsersByEmail';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    if (!currentUserEmail) {
      return throwError(() => new Error('No current user email found'));
    }

    return this.getUsersByEmail(currentUserEmail).pipe(
      map((response: PaginatedUsersDto) => {
        if (response && response.users.length > 0) {
          const apiUser = response.users[0];
          return apiUser.role ?? '';
        }
        return '';
      }),
      catchError((error) => {
        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'No users were returned');
        return throwError(() => error)
      })
    );
  }

  /**
   * Creates a new user and initializes related resources.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userService.createUser(user);
   *
   * // Use this in Interceptors, Guards and Functions
   * userService.createUser(user);
   * ```
   *
   * @param user - The user data to create
   * @returns Observable<void> - Emits when the user is successfully created
   */
  createUser(user: CreateUserDto): Observable<void> {
    this.functionName = 'createUser';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return this.userApiService.createApiUser(user).pipe(
      map((user: UserDto) => {
        if (!user.userId) {
          throw new UserError(UserErrorType.USER_NOT_CREATED);
        }

        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'User created:', user.email);
        return;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates an existing user and its related resources.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userService.updateUser(userId, user);
   *
   * // Use this in Interceptors, Guards and Functions
   * userService.updateUser(userId, user);
   * ```
   *
   * @param userId - The ID of the user to update
   * @param user - The updated user data
   * @returns Observable<void> - Emits when the user is successfully updated
   */
  updateUser(userId: string, user: UpdateUserDto): Observable<void> {
    this.functionName = 'updateUser';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const userRole = user.role ?? '';

    return this.userApiService.updateApiUser(userId, user).pipe(
      map(() => {
        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'User updated role:', userRole);
        return;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Deletes a user and its related resources.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userService.deleteUser(userId);
   *
   * // Use this in Interceptors, Guards and Functions
   * userService.deleteUser(userId);
   * ```
   *
   * @param userId - The ID of the user to delete
   * @returns Observable<void> - Emits when the user is successfully deleted
   */
  deleteUser(userId: string): Observable<void> {
    this.functionName = 'deleteUser';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return this.userApiService.deleteApiUser(userId).pipe(
      map(() => {
        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'User deleted:', userId);
        return;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
